import type { ReactNode } from 'react';

import { SegmentedControl } from '../../components/SegmentedControl';
import type {
  SegmentedControlProps,
  SegmentedControlSize,
} from '../../components/SegmentedControl/SegmentedControl.types';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import {
  type DeckSidePaneSegment,
  deckSidePaneSegmentItems,
} from './deck-side-pane-segments';

export interface DeckSidePaneHeaderProps<V extends string> {
  segments: readonly DeckSidePaneSegment<V>[];
  value: V;
  onValueChange: (value: V) => void;
  /** NATIVE: per-segment boxes, for a coach mark that spotlights one segment. */
  onItemLayout?: SegmentedControlProps<V>['onItemLayout'];
  size?: SegmentedControlSize;
  /**
   * Segments share the row's leftover width (the iPad pane, whose width is
   * the card's) instead of hugging their labels (web's pane).
   */
  fullWidth?: boolean;
  /**
   * The dock toggle that LEADS the tabs — collapse/expand the pane. A slot:
   * each platform wraps it in its own coach-mark target and picks its icon.
   */
  leading?: ReactNode;
  /** Right-aligned extra control (web's New Chat in the expanded chat view). */
  trailing?: ReactNode;
  /**
   * Wraps the segmented control — each platform's coach-mark targets for the
   * "Create AI Flashcards" / "Create Practise Tests" marks.
   */
  renderSegments?: (control: ReactNode) => ReactNode;
  className?: string;
  testID?: string;
}

/**
 * The header row of the deck's side pane — the Flashcards · Exam & Quizzes
 * group beside the document (Figma `174:2695` inline / `174:2995` expanded on
 * web, `66:3292` on the iPad): the dock toggle leading, the segment row, and an
 * optional trailing control.
 *
 * Lifted out of web's `SidePane` so the iPad pane and its full-width mode draw
 * the SAME row: the toggle's slot (always left of the tabs, never swapping
 * sides as the pane expands), the gap, and the `Label: N` count format. Which
 * segments exist is the caller's decision (web offers Chat, the iPad does not);
 * labels arrive pre-translated.
 */
export function DeckSidePaneHeader<V extends string>({
  segments,
  value,
  onValueChange,
  onItemLayout,
  size = 's',
  fullWidth = false,
  leading,
  trailing,
  renderSegments,
  className,
  testID,
}: DeckSidePaneHeaderProps<V>) {
  const control = (
    <SegmentedControl<V>
      size={size}
      fullWidth={fullWidth}
      value={value}
      onValueChange={onValueChange}
      onItemLayout={onItemLayout}
      items={deckSidePaneSegmentItems(segments)}
    />
  );
  return (
    <View
      className={cn('w-full flex-none flex-row items-center gap-2', className)}
      testID={testID}>
      <View className="min-w-0 flex-1 shrink flex-row items-center gap-3">
        {leading}
        {/* Full width: the segments take what the toggle leaves. Otherwise the
            wrapper hugs them, so the row reads toggle · tabs · space. */}
        <View className={fullWidth ? 'min-w-0 flex-1 shrink' : 'min-w-0 shrink flex-row'}>
          {renderSegments ? renderSegments(control) : control}
        </View>
      </View>
      {trailing ? (
        <View className="flex-none flex-row items-center gap-2">{trailing}</View>
      ) : null}
    </View>
  );
}
(DeckSidePaneHeader as { displayName?: string }).displayName = 'DeckSidePaneHeader';
